import { z } from 'zod';
import { SourceSystem } from '../enums.js';
import { DecimalString, IsoDateTime, UuidString } from '../primitives.js';

/**
 * BOM header.
 * - bom_template_key: stable ключ, объединяющий версии одного шаблона BOM.
 *   При clone наследуется; при activate — deactivate previous (9.1b).
 * - version: max+1 при clone.
 * - is_active: unique per (entity_id, bom_template_key) через partial DB index WHERE is_active=true.
 */
// S-FIX-4: BE list handler omits internal fields; made optional for prod parity.
export const Bom = z.object({
  id: UuidString,
  entity_id: UuidString.optional(),
  bom_template_key: z.string().min(1).optional(),
  version: z.number().int().nonnegative().optional(),
  name: z.string().min(1),
  item_id: UuidString.optional(),
  output_qty: DecimalString.optional(),
  uom: z.string().min(1).optional(),
  is_active: z.boolean().default(false),
  is_default: z.boolean().default(false),
  // FE-211 Pass 96: BE handler `routes.get('/boms')` returns `produced_item` denorm.
  produced_item: z
    .object({
      id: UuidString,
      sku: z.string().optional(),
      name: z.string(),
    })
    .nullable()
    .optional(),
  source_system: SourceSystem.optional(),
  source_document_id: z.string().nullable().optional(),
  source_system_ref: z.record(z.unknown()).nullable().optional(),
  fetched_at: IsoDateTime.nullable().optional(),
  created_at: IsoDateTime.optional(),
  updated_at: IsoDateTime.optional(),
});
export type Bom = z.infer<typeof Bom>;

/**
 * Тип потребления материала в рамках одного изделия BOM:
 * - per_unit — обычное потребление на единицу выпуска (qty × output_qty);
 * - setup    — разовый расход на старт смены/партии (например, клей в шприце, заправка дозатора);
 * - teardown — разовый расход на завершение смены/партии.
 * Setup/teardown материалы не масштабируются вместе с output_qty.
 */
export const BomItemConsumptionType = z.enum(['per_unit', 'setup', 'teardown']);
export type BomItemConsumptionType = z.infer<typeof BomItemConsumptionType>;

/**
 * Разбивка браковочного процента по физическим причинам.
 * Сумма не обязательно равна scrap_pct — scrap_pct это агрегат для расчёта,
 * breakdown — справочная детализация (для аналитики yield).
 */
export const BomItemScrapBreakdown = z.object({
  material_pct: DecimalString.default('0'),
  vacuum_pct: DecimalString.default('0'),
  spill_pct: DecimalString.default('0'),
  cut_pct: DecimalString.default('0'),
});
export type BomItemScrapBreakdown = z.infer<typeof BomItemScrapBreakdown>;

/** BOM item — input material (может быть sub-BOM для multi-level explode). */
export const BomItem = z.object({
  id: UuidString,
  entity_id: UuidString,
  bom_id: UuidString,
  item_id: UuidString,
  qty: DecimalString,
  uom: z.string().min(1),
  scrap_pct: DecimalString.default('0'),
  /** Детализация scrap по причинам (material/vacuum/spill/cut). Опционально. */
  scrap_breakdown: BomItemScrapBreakdown.nullable().default(null),
  /** per_unit (default) / setup / teardown. */
  consumption_type: BomItemConsumptionType.default('per_unit'),
  is_alternate: z.boolean().default(false),
  alternate_of_item_id: UuidString.nullable(),
  sub_bom_id: UuidString.nullable(),
  position: z.number().int().nonnegative(),
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});
export type BomItem = z.infer<typeof BomItem>;

/** BOM operation (routing — опциональный для простых BOM). */
export const BomOperation = z.object({
  id: UuidString,
  entity_id: UuidString,
  bom_id: UuidString,
  operation_name: z.string().min(1),
  workstation: z.string().nullable(),
  time_minutes: DecimalString,
  cost_per_minute: DecimalString.default('0'),
  position: z.number().int().nonnegative(),
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});
export type BomOperation = z.infer<typeof BomOperation>;
